import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';

const DoctorProfileBuilder = () => {
  const { user } = useUnifiedAuth();
  const API = process.env.REACT_APP_BACKEND_URL;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [doctorProfile, setDoctorProfile] = useState(null);

  // Comprehensive specialties list
  const specialtiesList = [
    'General Medicine',
    'Cardiology',
    'Dermatology',
    'Pediatrics',
    'Orthopedics',
    'Orthopedic Surgery',
    'Obstetrics & Gynecology',
    'Neurology',
    'Psychiatry',
    'Psychology',
    'ENT (Ear, Nose & Throat)',
    'Ophthalmology',
    'Dentistry',
    'Cosmetic Surgery',
    'Plastic Surgery',
    'Anesthesiology',
    'Emergency Medicine',
    'Family Medicine',
    'Internal Medicine',
    'Gastroenterology',
    'Endocrinology',
    'Rheumatology',
    'Pulmonology',
    'Nephrology',
    'Hematology',
    'Oncology',
    'Urology',
    'Radiology',
    'Pathology',
    'Physical Medicine & Rehabilitation',
    'Sports Medicine',
    'Pain Management',
    'Infectious Disease',
    'Allergy & Immunology'
  ].sort();

  // Common sub-specialties by specialty
  const subSpecialtiesBySpecialty = {
    'Cardiology': ['Interventional Cardiology', 'Electrophysiology', 'Heart Failure', 'Preventive Cardiology', 'Cardiac Imaging'],
    'Dermatology': ['Cosmetic Dermatology', 'Pediatric Dermatology', 'Dermatopathology', 'Laser Surgery'],
    'Orthopedics': ['Sports Medicine', 'Joint Replacement', 'Spine Surgery', 'Hand Surgery', 'Foot & Ankle Surgery'],
    'Neurology': ['Epilepsy', 'Stroke', 'Movement Disorders', 'Neuromuscular Medicine', 'Headache Medicine'],
    'Gastroenterology': ['Hepatology', 'Inflammatory Bowel Disease', 'Endoscopy', 'Motility Disorders'],
    'Oncology': ['Medical Oncology', 'Surgical Oncology', 'Radiation Oncology', 'Hematologic Oncology'],
    'Pediatrics': ['Neonatology', 'Pediatric Cardiology', 'Pediatric Neurology', 'Pediatric Endocrinology'],
    'Psychiatry': ['Child Psychiatry', 'Addiction Psychiatry', 'Geriatric Psychiatry', 'Forensic Psychiatry'],
    'Surgery': ['General Surgery', 'Trauma Surgery', 'Vascular Surgery', 'Thoracic Surgery', 'Bariatric Surgery']
  };

  // Get suggested sub-specialties based on selected specialty
  const getSuggestedSubSpecialties = () => {
    return subSpecialtiesBySpecialty[formData.speciality] || [];
  };
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    speciality: '',
    experience_years: '',
    qualification: '',
    clinic_name: '',
    clinic_address: '',
    location: '',
    consultation_fee: '',
    about: '',
    clinics: [],
    board_certifications: [],
    sub_specialties: [],
    languages: [],
    education: [],
    procedures_performed: '',
    online_consultation_available: false,
    in_clinic_available: true,
    availability: {
      sunday: [],
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: []
    }
  });
  
  // Temporary input states for lists
  const [newClinic, setNewClinic] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [newSubSpecialty, setNewSubSpecialty] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [newEducation, setNewEducation] = useState('');

  useEffect(() => {
    fetchDoctorProfile();
  }, []);

  const fetchDoctorProfile = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/doctor-profile`, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });
      
      if (response.data) {
        setDoctorProfile(response.data);
        setFormData({
          name: response.data.name || '',
          speciality: response.data.speciality || '',
          experience_years: response.data.experience_years || '',
          qualification: response.data.qualification || '',
          clinic_name: response.data.clinic_name || '',
          clinic_address: response.data.clinic_address || '',
          location: response.data.location || '',
          consultation_fee: response.data.consultation_fee || '',
          about: response.data.about || '',
          clinics: response.data.clinics || [],
          board_certifications: response.data.board_certifications || [],
          sub_specialties: response.data.sub_specialties || [],
          languages: response.data.languages || [],
          education: response.data.education || [],
          procedures_performed: response.data.procedures_performed || '',
          online_consultation_available: response.data.online_consultation_available || false,
          in_clinic_available: response.data.in_clinic_available !== false,
          availability: response.data.availability || {
            sunday: [],
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: []
          }
        });
      }
    } catch (error) {
      console.error('Error fetching doctor profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addToList = (listName, value, setter) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [listName]: [...prev[listName], value.trim()]
      }));
      setter('');
    }
  };

  const removeFromList = (listName, index) => {
    setFormData(prev => ({
      ...prev,
      [listName]: prev[listName].filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const profileData = {
        ...formData,
        experience_years: parseInt(formData.experience_years) || 0,
        consultation_fee: parseFloat(formData.consultation_fee) || 0,
        procedures_performed: parseInt(formData.procedures_performed) || 0
      };

      const response = await axios.put(
        `${API}/doctors/${doctorProfile?.id || 'new'}`,
        profileData,
        { headers: { Authorization: `Bearer ${user?.token}` } }
      );

      alert('✅ Profile saved successfully!');
      fetchDoctorProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('❌ Failed to save profile: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Doctor Profile Builder
        </h1>
        <p className="text-gray-700">Complete your professional profile to be visible to users on the website</p>
      </div>

        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Basic Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Dr. John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Speciality *</label>
              <select
                name="speciality"
                value={formData.speciality}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                required
              >
                <option value="">Select a specialty...</option>
                {specialtiesList.map((specialty) => (
                  <option key={specialty} value={specialty}>
                    {specialty}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Experience (years) *</label>
              <input
                type="number"
                name="experience_years"
                value={formData.experience_years}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="15"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Qualification *</label>
              <input
                type="text"
                name="qualification"
                value={formData.qualification}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="MBBS, MD"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Consultation Fee (KD) *</label>
              <input
                type="number"
                step="0.001"
                name="consultation_fee"
                value={formData.consultation_fee}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="150"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Procedures Performed</label>
              <input
                type="number"
                name="procedures_performed"
                value={formData.procedures_performed}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="1250"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">About</label>
            <textarea
              name="about"
              value={formData.about}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Tell users about yourself, your experience, and your approach to healthcare..."
            />
          </div>
        </div>

        {/* Clinic Information */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            Clinic Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Main Clinic Name</label>
              <input
                type="text"
                name="clinic_name"
                value={formData.clinic_name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Heart Care Center"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Clinic Address</label>
              <input
                type="text"
                name="clinic_address"
                value={formData.clinic_address}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="123 Medical Street"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Salmiya, Kuwait"
              />
            </div>
          </div>

          {/* Availability Options */}
          <div className="mt-4 flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="online_consultation_available"
                checked={formData.online_consultation_available}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm font-medium text-gray-700">🖥️ Online Consultation Available</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="in_clinic_available"
                checked={formData.in_clinic_available}
                onChange={handleInputChange}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm font-medium text-gray-700">🏥 In-Clinic Available</span>
            </label>
          </div>
        </div>

        {/* Additional Clinics */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Additional Clinics</h2>
          
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newClinic}
              onChange={(e) => setNewClinic(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addToList('clinics', newClinic, setNewClinic)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Kuwait Medical Center"
            />
            <button
              onClick={() => addToList('clinics', newClinic, setNewClinic)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {formData.clinics.map((clinic, index) => (
              <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center gap-2">
                {clinic}
                <button onClick={() => removeFromList('clinics', index)} className="text-blue-600 hover:text-blue-800">×</button>
              </span>
            ))}
          </div>
        </div>

        {/* Board Certifications */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Board Certifications</h2>
          
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newCertification}
              onChange={(e) => setNewCertification(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addToList('board_certifications', newCertification, setNewCertification)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Cardiology"
            />
            <button
              onClick={() => addToList('board_certifications', newCertification, setNewCertification)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {formData.board_certifications.map((cert, index) => (
              <span key={index} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm flex items-center gap-2">
                {cert}
                <button onClick={() => removeFromList('board_certifications', index)} className="text-green-600 hover:text-green-800">×</button>
              </span>
            ))}
          </div>
        </div>

        {/* Sub-Specialties */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Sub-Specialties</h2>
          
          {/* Suggested sub-specialties based on main specialty */}
          {formData.speciality && getSuggestedSubSpecialties().length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Suggested for {formData.speciality}:</p>
              <div className="flex flex-wrap gap-2">
                {getSuggestedSubSpecialties().map((suggested) => (
                  <button
                    key={suggested}
                    onClick={() => {
                      if (!formData.sub_specialties.includes(suggested)) {
                        setFormData(prev => ({
                          ...prev,
                          sub_specialties: [...prev.sub_specialties, suggested]
                        }));
                      }
                    }}
                    disabled={formData.sub_specialties.includes(suggested)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      formData.sub_specialties.includes(suggested)
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200'
                    }`}
                  >
                    {formData.sub_specialties.includes(suggested) ? '✓ ' : '+ '}{suggested}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Custom sub-specialty input */}
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newSubSpecialty}
              onChange={(e) => setNewSubSpecialty(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addToList('sub_specialties', newSubSpecialty, setNewSubSpecialty)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Or add custom sub-specialty..."
            />
            <button
              onClick={() => addToList('sub_specialties', newSubSpecialty, setNewSubSpecialty)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add
            </button>
          </div>

          {/* Selected sub-specialties */}
          <div className="flex flex-wrap gap-2">
            {formData.sub_specialties.map((sub, index) => (
              <span key={index} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm flex items-center gap-2">
                {sub}
                <button onClick={() => removeFromList('sub_specialties', index)} className="text-purple-600 hover:text-purple-800">×</button>
              </span>
            ))}
          </div>
        </div>

        {/* Languages */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Languages</h2>
          
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newLanguage}
              onChange={(e) => setNewLanguage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addToList('languages', newLanguage, setNewLanguage)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Arabic, English"
            />
            <button
              onClick={() => addToList('languages', newLanguage, setNewLanguage)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {formData.languages.map((lang, index) => (
              <span key={index} className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm flex items-center gap-2">
                {lang}
                <button onClick={() => removeFromList('languages', index)} className="text-yellow-600 hover:text-yellow-800">×</button>
              </span>
            ))}
          </div>
        </div>

        {/* Education */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Education</h2>
          
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newEducation}
              onChange={(e) => setNewEducation(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addToList('education', newEducation, setNewEducation)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="MBBS - Kuwait University (2005)"
            />
            <button
              onClick={() => addToList('education', newEducation, setNewEducation)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Add
            </button>
          </div>

          <div className="space-y-2">
            {formData.education.map((edu, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-800">{edu}</span>
                <button onClick={() => removeFromList('education', index)} className="text-red-600 hover:text-red-800">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Save Profile
              </>
            )}
          </button>
        </div>
      </div>
  );
};

export default DoctorProfileBuilder;
